/**
 * Code in this file is based on:
 * https://github.com/module-federation/module-federation-examples/blob/master/advanced-api/dynamic-remotes/app1/src/App.js
 *
 * With slightly changes to make it easier to use in our use case.
 */

import { Remote } from '../envs/types';

declare let __webpack_init_sharing__: (shareScope: string) => Promise<void>;
declare let __webpack_share_scopes__: { default: Record<string, unknown> };

/**
 * This method adds script tag with remote URL to load remote files.
 * It checks if scope is already defined in window to fetch remote file only once.
 *
 * @param url
 * @param scope
 * @return {Promise<unknown>}
 */
const loadRemoteScript = async (url: string, scope: string) => {
  return new Promise((resolve, reject) => {
    let script = document.querySelector<HTMLScriptElement>(
      `script[src="${url}"]`
    );

    if (script) {
      if (window[scope]) {
        resolve(window[scope]);
      } else {
        script.onload = () => resolve(window[scope]);
      }
      return;
    }
    script = document.createElement('script') as HTMLScriptElement;
    script.async = true;
    script.type = 'text/javascript';
    script.src = url;
    script.onload = () => resolve(window[scope]);
    script.onerror = () => {
      const error = `[${scope}] error loading remote: ${url}`;

      if (script) {
        document.head.removeChild(script);
      }

      reject(error);
    };
    document.head.appendChild(script);
  });
};

/**
 * This method initializes remote modules using webpack shared configuration.
 *
 * @param scope
 * @param module
 * @return {Promise<*>}
 */
const initRemoteModule = async <M>(scope: keyof Window, module: string) => {
  // Initializes the share scope. This fills it with known provided modules from this build and all remotes
  await __webpack_init_sharing__('default');

  const container = window[scope] as {
    init: (shared: Record<string, unknown>) => Promise<void>;
    get: (module: string) => Promise<() => M>;
  };

  // Initialize the container, it may provide shared modules
  await container.init(__webpack_share_scopes__.default);

  const factory = await container.get(module);
  const Module = factory();
  return Module;
};

interface RemoteModule extends Remote {
  module: string;
}

/**
 * That's a single method combining the two calls to make usage easier.
 * It firsts loads remote script, then initializes the module after script was loaded.
 */
const loadRemoteModule = <M>({ url, module, scope }: RemoteModule) => {
  return loadRemoteScript(url, scope).then(() =>
    initRemoteModule<M>(scope, module)
  );
};

export default loadRemoteModule;
